import { format } from 'date-fns'
import { sr } from 'date-fns/locale'
import type { TFunction } from 'i18next'
import { useQuery } from '@tanstack/react-query'
import React from 'react'
import { Badge, ListGroup, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { documentService } from '../../services/document.service'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import { formatDateTime } from '../../utils/dateFormatter'
import './DocumentHistory.scss'

export enum AuditLogAction {
  Create = 'create',
  Update = 'update',
  Delete = 'delete',
  Access = 'access',
}

interface AuditLogEntry {
  id: number
  timestamp: string
  actor?: {
    username: string
  }
  action: AuditLogAction
  changes: Record<string, any>
  display_changes?: Record<string, [unknown, unknown]>
  additional_data?: Record<string, unknown>
}

interface DocumentHistoryProps {
  documentId: number
  active?: boolean
}

const EMPTY_AUDIT_LOG_ENTRIES: AuditLogEntry[] = []
const isEmptyAuditValue = (v: unknown) => v === null || v === undefined || v === '' || v === 'None'
const getDisplayAuditValue = (value: any[]) => (isEmptyAuditValue(value?.[1]) ? value?.[0] : value?.[1])

const VERSION_EVENT_TYPE_I18N_KEYS: Record<string, string> = {
  document_created: 'version_event_document_created',
  version_upload_requested: 'version_event_version_upload_requested',
  version_created: 'version_event_version_created',
  version_restored: 'version_event_version_restored',
  document_updated: 'version_event_document_updated',
  document_moved_to_trash: 'version_event_document_moved_to_trash',
  document_restored_from_trash: 'version_event_document_restored_from_trash',
  document_permanently_deleted: 'version_event_document_permanently_deleted',
}

const PERMISSION_AUDIT_SUBKEYS = ['added_users', 'removed_users', 'added_groups', 'removed_groups'] as const

const EMAIL_BODY_PREVIEW_MAX_CHARS = 4000

const SPECIAL_FORMAT_KEYS = new Set([
  'document_downloaded',
  'document_opened',
  'document_printed',
  'document_relation_added',
  'document_relation_removed',
  'bulk_edit_action',
  'document_moved_to_trash',
  'document_restored_from_trash',
  'event',
])

function formatChangeValue(field: string, raw: unknown, translate: TFunction): string {
  if (isEmptyAuditValue(raw)) {
    return field === 'owner' ? translate('no_owner') : translate('empty_value')
  }
  return String(raw)
}

function formatTupleChangeDisplay(
  entry: AuditLogEntry,
  key: string,
  value: [unknown, unknown],
  translate: TFunction,
): string {
  const resolved = entry.display_changes?.[key]
  const oldRaw = resolved?.[0] ?? value[0]
  const newRaw = resolved?.[1] ?? value[1]
  const oldDisplay = formatChangeValue(key, oldRaw, translate)
  const newDisplay = formatChangeValue(key, newRaw, translate)

  if (isEmptyAuditValue(value[0]) && !isEmptyAuditValue(value[1])) {
    return newDisplay
  }
  if (oldDisplay === newDisplay) {
    return newDisplay
  }
  return `${oldDisplay} → ${newDisplay}`
}

function formatDocumentAuditDisplay(changeKey: string, raw: unknown, translate: TFunction): string {
  if (raw === null || raw === undefined) return ''
  const s = String(raw)
  if (changeKey === 'document_downloaded') {
    if (s === 'original') return translate('audit_document_downloaded_original')
    if (s === 'archive') return translate('audit_document_downloaded_archive')
    if (s === 'both') return translate('audit_document_downloaded_both')
    return s
  }
  if (changeKey === 'document_opened') {
    if (s === 'details') return translate('audit_document_opened_details')
    return s
  }
  if (changeKey === 'document_printed') {
    if (s === 'print_dialog') return translate('audit_document_printed_print_dialog')
    return s
  }
  if (changeKey === 'document_relation_added' || changeKey === 'document_relation_removed') {
    return s
  }
  if (changeKey === 'bulk_edit_action') {
    if (s === 'set_permissions') return translate('audit_bulk_edit_action_set_permissions')
    if (s === 'split') return translate('audit_bulk_edit_action_split')
    if (s === 'merge') return translate('audit_bulk_edit_action_merge')
    return s
  }
  if (changeKey === 'document_moved_to_trash') {
    return translate('audit_document_moved_to_trash_detail')
  }
  if (changeKey === 'document_restored_from_trash') {
    return translate('audit_document_restored_from_trash_detail')
  }
  if (changeKey === 'event') {
    const key = VERSION_EVENT_TYPE_I18N_KEYS[s]
    return key ? translate(key) : s.replace(/_/g, ' ')
  }
  return s
}

function hasEmailAuditDetails(additionalData: Record<string, unknown> | undefined): boolean {
  if (!additionalData || typeof additionalData !== 'object') return false
  return (
    Array.isArray(additionalData.addresses) ||
    typeof additionalData.subject === 'string' ||
    typeof additionalData.message === 'string'
  )
}

function formatVersionEventPayloadValue(key: string, raw: unknown, translate: TFunction): string {
  if (key === 'comment') {
    const s = String(raw ?? '').trim()
    return s === '' ? translate('empty_value') : s
  }
  if (key === 'version_number' || key === 'from_version_number' || key === 'to_version_number') {
    return `V${raw}`
  }
  if (isEmptyAuditValue(raw)) {
    return translate('empty_value')
  }
  return String(raw)
}

function renderVersionEventDetails(
  payload: Record<string, unknown>,
  parentKey: string,
  translate: TFunction,
): React.ReactNode {
  const docIds = Array.isArray(payload.document_ids) ? payload.document_ids : null
  if (docIds) {
    return (
      <li key={parentKey}>
        <span className="text-capitalize">{translate('documents')}</span>:&nbsp;
        <code className="text-primary">{docIds.join(', ')}</code>
      </li>
    )
  }

  const lines: React.ReactNode[] = []

  if (payload.from_version_number != null && payload.to_version_number != null) {
    lines.push(
      <li key={`${parentKey}-version-change`}>
        <span>{translate('audit_version_event_version_change')}</span>:&nbsp;
        <code className="text-primary">
          {`${formatVersionEventPayloadValue('from_version_number', payload.from_version_number, translate)} → ${formatVersionEventPayloadValue('to_version_number', payload.to_version_number, translate)}`}
        </code>
      </li>,
    )
  } else if (payload.version_number != null) {
    lines.push(
      <li key={`${parentKey}-version-number`}>
        <span className="text-capitalize">{translate('version')}</span>:&nbsp;
        <code className="text-primary">
          {formatVersionEventPayloadValue('version_number', payload.version_number, translate)}
        </code>
      </li>,
    )
  }

  if (typeof payload.file_name === 'string') {
    lines.push(
      <li key={`${parentKey}-file-name`}>
        <span>{translate('audit_version_event_file_name')}</span>:&nbsp;
        <code className="text-primary">
          {formatVersionEventPayloadValue('file_name', payload.file_name, translate)}
        </code>
      </li>,
    )
  }

  if ('comment' in payload) {
    lines.push(
      <li key={`${parentKey}-comment`}>
        <span className="text-capitalize">{translate('comment')}</span>:&nbsp;
        <code className="text-primary">{formatVersionEventPayloadValue('comment', payload.comment, translate)}</code>
      </li>,
    )
  }

  if (lines.length > 0) {
    return <>{lines}</>
  }

  return (
    <li key={parentKey}>
      <span className="text-capitalize">{translate('details', { defaultValue: 'Details' })}</span>:&nbsp;
      <code className="text-primary">{JSON.stringify(payload)}</code>
    </li>
  )
}

const DocumentHistory: React.FC<DocumentHistoryProps> = ({ documentId, active = true }) => {
  const { t, i18n } = useTranslation()
  const canViewHistory = permissionsService.currentUserCan(PermissionAction.View, PermissionType.History)
  const { data: queryEntries, isLoading } = useQuery<AuditLogEntry[]>({
    queryKey: ['document-history', documentId],
    queryFn: () => documentService.getHistory(documentId),
    enabled: Boolean(documentId) && active && canViewHistory,
    refetchOnMount: 'always',
  })
  const entries = queryEntries ?? EMPTY_AUDIT_LOG_ENTRIES

  if (!canViewHistory) {
    return (
      <div className="d-flex justify-content-center">
        <span className="fst-italic text-muted">{t('you_dont_have_permissions_to_do_that')}</span>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="d-flex">
        <Spinner animation="border" size="sm" role="status" className="fw-normal" />
      </div>
    )
  }

  const locale = i18n.language === 'sr' ? sr : undefined

  return (
    <ListGroup>
      {entries.length === 0 ? (
        <ListGroup.Item>
          <div className="d-flex justify-content-center">
            <span className="fst-italic">{t('no_entries_found')}</span>
          </div>
        </ListGroup.Item>
      ) : (
        entries.map((entry) => (
          <ListGroup.Item key={entry.id}>
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">{formatDateTime(entry.timestamp)}</span>
              {entry.actor ? <span className="ms-3 fst-italic">{entry.actor.username}</span> : <span className="ms-3 fst-italic">{t('system')}</span>}
              <Badge
                bg={entry.action === AuditLogAction.Create ? 'primary' : entry.action === AuditLogAction.Access ? 'success' : 'secondary'}
                className="ms-auto text-capitalize"
              >
                {t(entry.action)}
              </Badge>
            </div>
            <ul className="mt-2">
              {Object.entries(entry.changes).map(([key, value]) => {
                if (value && typeof value === 'object') {
                  if (value['type'] === 'm2m') {
                    const operationKey = value['operation']
                    return (
                      <li key={key}>
                        <span className="fst-italic text-capitalize">{t(operationKey)}</span>
                        &nbsp;
                        <span className="text-capitalize">{t(key) || key.replace(/_/g, ' ')}</span>:&nbsp;
                        <code className="text-primary">{value['objects'].join(', ')}</code>
                      </li>
                    )
                  } else if (value['type'] === 'custom_field') {
                    const op = String(value['operation'] ?? 'update').toLowerCase()
                    const opI18nKey =
                      op === 'create'
                        ? 'audit_custom_field_created'
                        : op === 'delete'
                          ? 'audit_custom_field_deleted'
                          : 'audit_custom_field_updated'
                    const emptyPlaceholder = t('empty_value')
                    const renderPrimitive = (x: unknown) =>
                      x === null || x === undefined || x === '' ? emptyPlaceholder : String(x)
                    const oldVal = value['old']
                    const newVal = value['new']
                    const valueDisplay =
                      op === 'create'
                        ? renderPrimitive(newVal)
                        : op === 'delete'
                          ? renderPrimitive(oldVal)
                          : `${renderPrimitive(oldVal)} → ${renderPrimitive(newVal)}`
                    return (
                      <li key={key}>
                        <div>
                          <span className="text-capitalize">{t('event')}</span>:&nbsp;
                          <code className="text-primary">{t(opI18nKey)}</code>
                        </div>
                        <div>
                          <span>{value['field']}</span>:&nbsp;
                          <code className="text-primary">{valueDisplay}</code>
                        </div>
                      </li>
                    )
                  } else if (value['type'] === 'note') {
                    const op = value['operation']
                    const operation =
                      op === 'deleted' ? 'note_deleted' : op === 'updated' ? 'note_updated' : 'note_added'
                    const prevContent = value['previous_content']
                    return (
                      <>
                        <li key={`${key}-event`}>
                          <span className="text-capitalize">{t('event')}</span>:&nbsp;
                          <code className="text-primary">{t(operation)}</code>
                        </li>
                        {op === 'updated' && prevContent !== undefined ? (
                          <>
                            <li key={`${key}-prev`}>
                              <span className="text-capitalize">{t('note_previous_content')}</span>:&nbsp;
                              <code className="text-primary" style={{ whiteSpace: 'pre-wrap' }}>
                                {String(prevContent)}
                              </code>
                            </li>
                            <li key={`${key}-new`}>
                              <span className="text-capitalize">{t('note_new_content')}</span>:&nbsp;
                              <code className="text-primary" style={{ whiteSpace: 'pre-wrap' }}>
                                {String(value['content'] ?? '')}
                              </code>
                            </li>
                          </>
                        ) : (
                          <li key={`${key}-content`}>
                            <span className="text-capitalize">{t('content')}</span>:&nbsp;
                            <code className="text-primary" style={{ whiteSpace: 'pre-wrap' }}>
                              {String(value['content'] ?? '')}
                            </code>
                          </li>
                        )}
                      </>
                    )
                  } else if (value['type'] === 'document_permissions') {
                    const actions = ['view', 'change'] as const
                    return (
                      <>
                        <li key={`${key}-event`}>
                          <span className="text-capitalize">{t('event')}</span>:&nbsp;
                          <code className="text-primary">{t('audit_permissions_changed')}</code>
                        </li>
                        {actions.flatMap((action) => {
                          const actionData = value[action] as Record<string, string[]> | undefined
                          if (!actionData) return []
                          return PERMISSION_AUDIT_SUBKEYS.flatMap((subKey) => {
                            const names = actionData[subKey]
                            if (!Array.isArray(names) || names.length === 0) return []
                            const i18nKey = `audit_permissions_${action}_${subKey}`
                            return (
                              <li key={`${key}-${action}-${subKey}`}>
                                <span>{t(i18nKey)}</span>:&nbsp;
                                <code className="text-primary">{names.join(', ')}</code>
                              </li>
                            )
                          })
                        })}
                      </>
                    )
                  } else if (value['type'] === 'version_event_details') {
                    const payload = (value['payload'] ?? {}) as Record<string, unknown>
                    return renderVersionEventDetails(payload, key, t)
                  } else if (value.length === 2) {
                    const normalizedKey = key.toLowerCase().replace(/\s+/g, '_')
                    const label = t(normalizedKey, { defaultValue: key.replace(/_/g, ' ') })
                    const displayValue = getDisplayAuditValue(value)
                    const isVersionCreateEvent =
                      key === 'version' &&
                      typeof displayValue === 'string' &&
                      (displayValue.toLowerCase().includes('created version') || displayValue.toLowerCase().includes('kreirana verzija'))

                    if (key === 'document_emailed' && hasEmailAuditDetails(entry.additional_data)) {
                      const ad = entry.additional_data as {
                        addresses?: string[]
                        subject?: string
                        message?: string
                        use_archive_version?: boolean
                      }
                      const addrs = Array.isArray(ad.addresses) ? ad.addresses.join(', ') : ''
                      const subjectStr = typeof ad.subject === 'string' ? ad.subject : ''
                      const bodyStr = typeof ad.message === 'string' ? ad.message : ''
                      const bodyPreview =
                        bodyStr.length > EMAIL_BODY_PREVIEW_MAX_CHARS
                          ? `${bodyStr.slice(0, EMAIL_BODY_PREVIEW_MAX_CHARS)}…`
                          : bodyStr
                      const useArchive = ad.use_archive_version === true
                      return (
                        <li key={key}>
                          <span className="text-capitalize">{label}</span>
                          <div className="small mt-1 text-body-secondary">
                            <div>
                              <span className="fw-semibold">{t('to')}:</span> {addrs || '—'}
                            </div>
                            <div className="mt-1">
                              <span className="fw-semibold">{t('subject')}:</span> {subjectStr || '—'}
                            </div>
                            <div className="mt-2 fw-semibold">{t('email_body')}:</div>
                            <pre className="mb-0 mt-1 small bg-light p-2 rounded text-body" style={{ whiteSpace: 'pre-wrap' }}>
                              {bodyPreview || '—'}
                            </pre>
                            <div className="mt-2 small">
                              <span className="fw-semibold">{t('history_email_attachment')}:</span>{' '}
                              {useArchive ? t('audit_email_used_archive_attachment') : t('audit_email_used_original_attachment')}
                            </div>
                          </div>
                        </li>
                      )
                    }

                    const formattedValue = SPECIAL_FORMAT_KEYS.has(key)
                      ? formatDocumentAuditDisplay(key, displayValue, t)
                      : formatTupleChangeDisplay(entry, key, value as [unknown, unknown], t)

                    const bulkDownload =
                      key === 'document_downloaded' &&
                      entry.additional_data &&
                      (entry.additional_data as { bulk_download?: boolean }).bulk_download === true

                    return (
                      <li key={key}>
                        <span className="text-capitalize">{label}</span>:&nbsp;
                        {key === 'content' ? (
                          <code className="text-primary">{displayValue ? String(displayValue).substring(0, 100) : ''}...</code>
                        ) : (
                          <>
                            <code className="text-primary">{formattedValue as React.ReactNode}</code>
                            {bulkDownload ? (
                              <span className="text-muted ms-1 small">({t('audit_document_downloaded_bulk_zip')})</span>
                            ) : null}
                            {isVersionCreateEvent ? (
                              <span className="text-muted ms-2">
                                ({t('created_by', { defaultValue: 'created by' })} {entry.actor?.username || t('system')},{' '}
                                {format(new Date(entry.timestamp), 'PPpp', { locale })})
                              </span>
                            ) : null}
                          </>
                        )}
                      </li>
                    )
                  }
                }
                return null
              })}
            </ul>
          </ListGroup.Item>
        ))
      )}
    </ListGroup>
  )
}

export default DocumentHistory
